'use client';

import { useState, useEffect, useCallback } from 'react';
import { ActivityEvent, getActivityEvents, generateActivityEvent, addActivityEvent, seedInitialActivity } from '@/lib/bookingActivityFeed';

const COLOR_CLASSES: Record<string, { bg: string; text: string; border: string }> = {
  green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  gray: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
};

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function LiveBookingFeed() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isLive, setIsLive] = useState(true);
  const [newCount, setNewCount] = useState(0);

  const loadEvents = useCallback(() => {
    seedInitialActivity();
    setEvents(getActivityEvents().slice(0, 8));
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      const newEvent = generateActivityEvent();
      addActivityEvent(newEvent);
      setEvents(getActivityEvents().slice(0, 8));
      setNewCount(prev => prev + 1);
      setTimeout(() => setNewCount(prev => Math.max(0, prev - 1)), 5000);
    }, 8000 + Math.random() * 7000);
    return () => clearInterval(interval);
  }, [isLive]);

  return (
    <section className="py-12 bg-gradient-to-b from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              {isLive && <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75"></div>}
            </div>
            <div>
              <h2 className="font-heading text-2xl font-black text-foreground">Live Booking Activity</h2>
              <p className="text-sm text-muted-foreground">Real-time bookings &amp; events across the platform</p>
            </div>
            {newCount > 0 && (
              <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full animate-bounce">+{newCount} new</span>
            )}
          </div>
          <button
            onClick={() => setIsLive(prev => !prev)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all ${
              isLive ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            {isLive ? 'Live' : 'Paused'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {events.map((event, idx) => {
            const colors = COLOR_CLASSES[event.color] || COLOR_CLASSES.gray;
            return (
              <div
                key={event.id}
                className={`rounded-xl border p-4 transition-all duration-500 ${colors.bg} ${colors.border} ${
                  idx === 0 && event.isNew ? 'ring-2 ring-primary/30 scale-[1.02]' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-2xl">{event.icon}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(event.timestamp)}</span>
                </div>
                <p className={`text-sm font-bold ${colors.text} mb-1`}>{event.title}</p>
                <p className="text-xs text-muted-foreground leading-snug">{event.description}</p>
                {event.bookingRef && (
                  <p className="text-xs font-mono text-muted-foreground mt-1 opacity-70">#{event.bookingRef}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
          <span>🌍 <strong className="text-foreground">24</strong> countries served today</span>
          <span>✈️ <strong className="text-foreground">142</strong> bookings this week</span>
          <span>⭐ <strong className="text-foreground">4.9/5</strong> customer rating</span>
          <span>💷 <strong className="text-foreground">£89k+</strong> saved for customers</span>
        </div>
      </div>
    </section>
  );
}
